// Dashboard - список проектов (из orig/main.py)

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { projectsApi, uploadApi, scenariosApi, tasksApi } from '../services/api';
import type { Project } from '../types';
import './Dashboard.css';

const Dashboard: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [creating, setCreating] = useState(false);

  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const data = await projectsApi.list();
      setProjects(data);
    } catch (error) {
      console.error('Failed to load projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!videoFile) {
      alert('Пожалуйста, выберите видео файл');
      return;
    }

    setCreating(true);
    setUploadProgress(10);
    setUploadStatus('Загрузка видео...');

    try {
      // Сначала создаем проект
      setUploadProgress(20);
      setUploadStatus('Создание проекта...');
      const createResult = await projectsApi.create(videoFile.name.replace(/\.[^/.]+$/, ''));

      if (!createResult.success || !createResult.project_id) {
        throw new Error(createResult.error || 'Failed to create project');
      }

      const projectId = createResult.project_id;

      // Загружаем видео
      setUploadProgress(40);
      setUploadStatus('Загрузка видео...');
      const uploadResult = await uploadApi.uploadTempFile(videoFile);

      if (!uploadResult.success) {
        throw new Error(uploadResult.error || 'Failed to upload video');
      }

      // Запускаем анализ видео через Gemini AI
      setUploadProgress(60);
      setUploadStatus('Анализ видео через Gemini AI... Это может занять 1-2 минуты.');
      const analyzeResult = await scenariosApi.analyze(projectId, uploadResult.url);

      if (!analyzeResult.success) {
        throw new Error(analyzeResult.error || 'Failed to start video analysis');
      }

      const taskId = analyzeResult.task_id;

      if (!taskId) {
        throw new Error('No task_id returned from analysis');
      }

      // Отслеживаем прогресс анализа
      const checkProgress = async () => {
        try {
          const taskStatus = await tasksApi.getStatus(taskId, projectId);

          if (taskStatus.status === 'completed') {
            setUploadProgress(100);
            setUploadStatus('Анализ завершен! Перенаправление...');
            setTimeout(() => {
              setShowCreateModal(false);
              setVideoFile(null);
              setUploadProgress(0);
              setUploadStatus('');
              setCreating(false);
              loadProjects(); // Обновить список проектов
            }, 1000);
          } else if (taskStatus.status === 'failed') {
            throw new Error(taskStatus.error || 'Analysis failed');
          } else {
            // Обновить прогресс
            if (taskStatus.progress) {
              setUploadProgress(60 + (taskStatus.progress / 100) * 40); // 60-100%
            }
            if (taskStatus.result?.message) {
              setUploadStatus(taskStatus.result.message);
            }
            // Продолжить проверку через 2 секунды
            setTimeout(checkProgress, 2000);
          }
        } catch (error: any) {
          console.error('Failed to check task status:', error);
          // Продолжить проверку даже при ошибке
          setTimeout(checkProgress, 2000);
        }
      };

      // Начать отслеживание прогресса
      checkProgress();

    } catch (error: any) {
      console.error('Failed to create project:', error);
      alert('Ошибка при создании проекта: ' + error.message);
      setUploadProgress(0);
      setUploadStatus('');
      setCreating(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleDeleteProject = async (projectId: number, projectName: string) => {
    const confirmed = window.confirm(
      `Вы уверены, что хотите удалить проект "${projectName}"?\n\nЭто действие нельзя отменить.`
    );

    if (!confirmed) return;

    try {
      await projectsApi.delete(projectId);
      alert('Проект успешно удален');
      loadProjects(); // Обновить список
    } catch (error: any) {
      console.error('Failed to delete project:', error);
      alert('Ошибка при удалении проекта: ' + error.message);
    }
  };

  if (loading) {
    return <div className="loading-spinner"></div>;
  }

  return (
    <div className="container">
      <div className="header">
        <div className="header-left">
          <h1>Local Project Manager</h1>
        </div>
        <div className="header-right">
          <button onClick={() => setShowCreateModal(true)} className="btn btn-primary" style={{ marginRight: '10px' }}>
            + Create Project
          </button>
          <div className="user-info">{user?.username}</div>
          <button onClick={loadProjects} className="btn btn-secondary">Refresh</button>
          <button onClick={handleLogout} className="btn btn-secondary">Logout</button>
        </div>
      </div>

      <div className="projects-grid">
        {projects.length === 0 ? (
          <p style={{ textAlign: 'center', color: '#6c757d' }}>No projects found</p>
        ) : (
          projects.map((project) => (
            <div key={project.id} className="project-card">
              <h3>{project.name}</h3>
              <div className="stats">
                <span>Scenes: {project.scenes_count || 0}</span>
              </div>
              <div className="project-actions" style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                <button
                  onClick={() => navigate(`/project/${project.id}`)}
                  className="btn btn-primary"
                  style={{ flex: 1 }}
                >
                  👁️ View
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteProject(project.id, project.name);
                  }}
                  className="btn btn-danger"
                  style={{ flex: 1 }}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => !creating && setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create Project from Video</h3>
              <button className="modal-close" onClick={() => !creating && setShowCreateModal(false)}>×</button>
            </div>

            <div className="modal-body">
              <p style={{ color: '#666', marginBottom: '20px' }}>
                Upload a video and AI will analyze it to create a project with scenes and character descriptions.
              </p>

              <div className="form-group">
                <label>Select Video File:</label>
                <input
                  type="file"
                  accept="video/*"
                  onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
                  disabled={creating}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
              </div>

              {creating && (
                <div style={{ marginTop: '20px' }}>
                  <div style={{ background: '#f0f0f0', borderRadius: '4px', height: '30px', overflow: 'hidden' }}>
                    <div
                      style={{
                        background: '#4CAF50',
                        height: '100%',
                        width: `${uploadProgress}%`,
                        transition: 'width 0.3s'
                      }}
                    ></div>
                  </div>
                  <p style={{ textAlign: 'center', marginTop: '10px', color: '#666' }}>{uploadStatus}</p>
                </div>
              )}
            </div>

            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <button onClick={() => setShowCreateModal(false)} className="btn btn-secondary" disabled={creating}>
                Cancel
              </button>
              <button
                onClick={handleCreateProject}
                className="btn btn-primary"
                disabled={creating || !videoFile}
                style={{ marginLeft: '10px' }}
              >
                {creating ? 'Creating...' : 'Create Project'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
